import React, { useEffect, useState } from 'react';
import { useAuth, api } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  DollarSign, 
  Receipt, 
  TrendingUp, 
  Wallet,
  Plus,
  Eye,
  ArrowRight,
  Calendar,
  AlertTriangle,
  MapPin,
  Clock,
  User,
  Phone,
  Globe,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    todayBills: 0,
    todayRevenue: 0,
    cashBalance: 0,
    bookingCashBalance: 0,
    recentBills: []
  });
  const [loading, setLoading] = useState(true);
  const [missingAlert, setMissingAlert] = useState(null);
  const [alertExpanded, setAlertExpanded] = useState(false);

  useEffect(() => {
    if (!user) return;
    fetchDashboardData();
    fetchMissingBookings();
  }, [user?.id]);

  const fetchMissingBookings = async () => {
    try {
      const res = await api.get('/website-bookings/yesterday-missing');
      if (res.data.total_missing > 0) {
        setMissingAlert(res.data);
      }
    } catch {
      // Silently fail - alert is non-critical
    }
  };

  const fetchDashboardData = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // Fetch today's bills
      const billsResponse = await api.get('/bills');
      const allBills = billsResponse.data;
      const todayBills = allBills.filter(bill => bill.date === today);
      const todayRevenue = todayBills.reduce((sum, bill) => sum + bill.actual_amount, 0);

      // Get yesterday's date
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0];
      
      const yesterdayBills = allBills.filter(bill => bill.date === yesterdayStr);

      // Fetch cash balance and booking cash balance
      let cashBalance = 0;
      let bookingCashBalance = 0;
      
      if (user?.role === 'admin') {
        // For admin, get all uncollected and active bookings
        try {
          const bookingsResponse = await api.get('/cash-bookings');
          const activeUncollectedBookings = bookingsResponse.data.filter(b => !b.collected && b.status === 'active');
          bookingCashBalance = activeUncollectedBookings.reduce((sum, b) => sum + (b.advance_cash || 0), 0);
        } catch (error) {
          console.error('Failed to fetch booking cash balance');
        }
      } else if (user?.branch_id) {
        // For managers/regional managers, get branch-specific data
        const cashResponse = await api.get(`/cash-balance?branch_id=${user.branch_id}`);
        cashBalance = cashResponse.data.current_balance;
        bookingCashBalance = cashResponse.data.total_booking_cash || 0;
      }

      setStats({
        todayBills: todayBills.length,
        yesterdayBills: yesterdayBills.length,
        todayRevenue,
        cashBalance,
        bookingCashBalance,
        recentBills: allBills.slice(0, 5)
      });
    } catch (error) {
      toast.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatAlertDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' });
  };


  const statCards = [
    {
      title: "Today's Bills",
      value: stats.todayBills,
      icon: Receipt,
      color: 'from-blue-500 to-blue-600',
      testId: 'today-bills-stat'
    },
    {
      title: "Yesterday's Bills",
      value: stats.yesterdayBills || 0,
      icon: Receipt,
      color: 'from-orange-500 to-orange-600',
      show: user?.role !== 'admin',
      testId: 'yesterday-bills-stat'
    },
    {
      title: "Today's Revenue",
      value: `₹${stats.todayRevenue.toFixed(2)}`,
      icon: DollarSign,
      color: 'from-green-500 to-green-600',
      show: user?.role === 'admin',
      testId: 'today-revenue-stat'
    },
    {
      title: 'Cash Balance',
      value: `₹${stats.cashBalance.toFixed(2)}`,
      icon: Wallet,
      color: 'from-purple-500 to-purple-600',
      show: user?.role !== 'admin',
      testId: 'cash-balance-stat'
    },
    {
      title: 'Booking Cash Balance',
      value: `₹${stats.bookingCashBalance.toFixed(2)}`,
      icon: Calendar,
      color: 'from-amber-500 to-amber-600',
      testId: 'booking-cash-balance-stat'
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Auditor view - simplified dashboard
  if (user?.role === 'auditor') {
    return (
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Space Grotesk' }}>
            Welcome back, {user?.username}!
          </h1>
          <p className="text-slate-600 mt-1">Use the View Bills page to audit bills from your assigned branches.</p>
        </div>

        {/* Quick Action for Auditor */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl" style={{ fontFamily: 'Space Grotesk' }}>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => navigate('/view-bills')}
              className="h-auto py-6 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white w-full"
            >
              <div className="flex items-center justify-center gap-3">
                <Eye className="w-6 h-6" />
                <span className="font-medium text-lg">View Bills</span>
                <ArrowRight className="w-5 h-5" />
              </div>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Space Grotesk' }} data-testid="dashboard-title">
          Welcome back, {user?.username}!
        </h1>
        <p className="text-slate-600 mt-1">Here's what's happening in your {user?.branch_name || 'theaters'} today.</p>
      </div>

      {/* Missing Bills Alert for Yesterday */}
      {missingAlert && missingAlert.total_missing > 0 && (
        <Card className="border-2 border-red-300 bg-red-50 shadow-lg" data-testid="missing-bills-alert">
          <CardContent className="pt-6 pb-4">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-full bg-red-100 mt-0.5">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-red-800">
                    {missingAlert.total_missing} Missing Bill{missingAlert.total_missing > 1 ? 's' : ''} from Yesterday
                  </h3>
                  <p className="text-sm text-red-600 mt-0.5">
                    {missingAlert.total_missing} of {missingAlert.total_yesterday} paid bookings on {formatAlertDate(missingAlert.date)} don't have bills yet.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  data-testid="view-missing-bookings-btn"
                  size="sm"
                  onClick={() => navigate('/website-bookings')}
                  className="bg-red-600 hover:bg-red-700 text-white gap-1"
                >
                  <Globe size={14} />
                  View All
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setAlertExpanded(!alertExpanded)}
                  className="text-red-700 hover:bg-red-100"
                >
                  {alertExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </Button>
              </div>
            </div>

            {alertExpanded && (
              <div className="mt-4 space-y-3" data-testid="missing-bills-details">
                {Object.entries(missingAlert.branches).map(([branch, bookings]) => (
                  <div key={branch} className="bg-white rounded-lg p-3 border border-red-200">
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin size={14} className="text-red-500" />
                      <span className="font-semibold text-red-800 text-sm">{branch}</span>
                      <Badge variant="destructive" className="text-xs bg-red-100 text-red-700 hover:bg-red-100">
                        {bookings.length} missing
                      </Badge>
                    </div>
                    <div className="space-y-1.5">
                      {bookings.map((b) => (
                        <div key={b.booking_id} className="flex items-center justify-between text-xs text-slate-700 pl-5">
                          <div className="flex items-center gap-3">
                            <span className="font-mono text-slate-400">{b.booking_id}</span>
                            <span className="flex items-center gap-1">
                              <User size={10} className="text-slate-400" />
                              {b.customer_name}
                            </span>
                            <span className="flex items-center gap-1">
                              <Phone size={10} className="text-slate-400" />
                              {b.phone_number}
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-medium">{b.theater}</span>
                            <span className="flex items-center gap-1 text-slate-500">
                              <Clock size={10} />
                              {b.time_slot}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.filter(card => card.show !== false).map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title} className="card-hover border-0 shadow-lg" data-testid={stat.testId}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600">{stat.title}</p>
                    <h3 className="text-3xl font-bold mt-2 text-slate-900">{stat.value}</h3>
                  </div>
                  <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.color} shadow-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Actions */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="text-xl" style={{ fontFamily: 'Space Grotesk' }}>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(user?.role !== 'manager' && user?.role !== 'regional_manager') ? (
              <Button
                data-testid="quick-action-add-bill"
                onClick={() => navigate('/add-bill')}
                className="h-auto py-6 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white"
              >
                <div className="flex flex-col items-center gap-2">
                  <Plus className="w-6 h-6" />
                  <span className="font-medium">Add New Bill</span>
                </div>
              </Button>
            ) : (
              <Button
                data-testid="quick-action-website-bookings"
                onClick={() => navigate('/website-bookings')}
                className="h-auto py-6 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white"
              >
                <div className="flex flex-col items-center gap-2">
                  <Globe className="w-6 h-6" />
                  <span className="font-medium">Update Bills</span>
                </div>
              </Button>
            )}
            <Button
              data-testid="quick-action-view-bills"
              onClick={() => navigate('/view-bills')}
              variant="outline"
              className="h-auto py-6 border-2"
            >
              <div className="flex flex-col items-center gap-2">
                <Eye className="w-6 h-6" />
                <span className="font-medium">View Bills</span>
              </div>
            </Button>
            <Button
              data-testid="quick-action-cash-handover"
              onClick={() => navigate('/cash-handover')}
              variant="outline"
              className="h-auto py-6 border-2"
            >
              <div className="flex flex-col items-center gap-2">
                <Wallet className="w-6 h-6" />
                <span className="font-medium">Cash Handover</span>
              </div>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Bills */}
      {stats.recentBills.length > 0 && (
        <Card className="border-0 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-xl" style={{ fontFamily: 'Space Grotesk' }}>Recent Bills</CardTitle>
            <Button
              data-testid="view-all-bills-btn"
              onClick={() => navigate('/view-bills')}
              variant="ghost"
              size="sm"
              className="gap-2"
            >
              View All
              <ArrowRight size={16} />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.recentBills.map((bill) => (
                <div
                  key={bill.id}
                  data-testid={`recent-bill-${bill.id}`}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <div>
                    <p className="font-medium text-slate-900">{bill.theater_name}</p>
                    <p className="text-sm text-slate-600">{bill.date} • {bill.time_slot}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-slate-900">₹{bill.actual_amount.toFixed(2)}</p>
                    <p className="text-xs text-slate-500 capitalize">{bill.payment_mode}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
